// libs
import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import useToggle from '@lyearn/core/hooks/useToggle';
import { TypographyWithTooltip } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
// components
import { Popover, Stack } from '@lyearn/ui';
import { Link } from '@/components/Link';
import * as SidebarAccordion from '@/components/Panel/SidebarAccordion';
import useNavigation from '@/hooks/useNavigation';

// hooks
import { SidebarItemProps, SideBarMode } from '../../hooks/useSidebar';
import { useSidebarFlyout } from './FlyoutPanel/context';
import FlyoutPanel from './FlyoutPanel';
import { FLYOUT_PANEL_WIDTH, sidebarItemStyles } from './style';
import { SidebarNavItem, SidebarNavSubItem } from './types';
import { isSidebarItemActive } from './utils';

export const SIDEBAR_ITEM_ANIMATION = {
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
  initial: { height: 0, opacity: 0 },
  transition: {
    layout: {
      type: 'spring',
      bounce: 0.4,
    },
  },
};

const FLYOUT_PANEL_CLASSES = {
  Popover: {
    background: 'bg-white',
    border: 'border border-solid border-border',
    rounded: 'rounded-12',
    shadow: 'shadow-02',
  },
};

const OPEN_FLYOUT_KEYS = ['ArrowRight', 'ArrowDown'];

export interface RenderSidebarItemProps {
  to?: string;
  onClick?: () => void;
  onKeyDown?: React.KeyboardEventHandler;
  isActive: boolean;
  isDisabled?: boolean;
  icon?: JSX.Element;
  text: string;
  className?: string; // use this for margin purpose
  overrides?: {
    root?: string;
    icon?: string;
    text?: string;
  };
  mode?: SideBarMode;
  action?: SidebarItemProps['action'];
  isChild?: boolean;
  isExpandable?: boolean;
  showItems?: boolean;
  setShowItems?: Dispatch<SetStateAction<boolean>>;
  hasFlyout?: boolean;
  isFlyoutOpen?: boolean;
  rootRef?: React.Ref<HTMLDivElement>;
}

export const renderSidebarItem = ({
  to,
  isActive,
  isDisabled,
  onClick,
  onKeyDown,
  icon,
  text,
  className,
  overrides,
  action,
  mode,
  isChild,
  isExpandable,
  showItems,
  hasFlyout,
  isFlyoutOpen,
  rootRef,
}: RenderSidebarItemProps) => {
  const TextElem = (
    <TypographyWithTooltip
      disableInteractive
      className={overrides?.text || 'truncate'}
      color={isActive ? 'text-text-primary' : 'text-text-secondary'}
      numberOfLines={1}
      variant={mode === 'learner' || !isActive ? 'body-short-02' : 'label-02'}>
      {text}
    </TypographyWithTooltip>
  );

  const InnerElem = (
    <Stack
      alignItems="center"
      className={cn('z-10 w-full truncate', isDisabled ? 'cursor-default' : 'cursor-pointer')}
      justifyContent="between">
      <Stack alignItems="center" className="truncate">
        <Stack
          alignItems="center"
          className={
            overrides?.icon ||
            cn(
              isActive ? 'text-icon-active' : 'text-icon-secondary',
              isChild ? 'me-10 w-6' : 'h-32 w-32 flex-shrink-0',
            )
          }
          justifyContent="center">
          {icon}
        </Stack>
        {TextElem}
      </Stack>
      {isExpandable ? (
        <SidebarAccordion.Chevron iconColor="icon-placeholder" isExpanded={showItems} />
      ) : null}
      {action}
    </Stack>
  );

  const classes =
    overrides?.root ||
    cn(
      sidebarItemStyles,
      className,
      'group relative flex h-32 flex-shrink-0 overflow-hidden truncate rounded-8',
      isChild ? 'px-0' : 'mb-6 pe-4',
      isDisabled ? 'pointer-events-none opacity-50' : 'active:bg-interactive-secondary-hover',
      {
        'bg-interactive-secondary-hover': mode === 'learner' && isActive,
        'bg-bg-hover': mode !== 'learner' && isActive && !isDisabled,
        'mt-2': mode === 'learner',
      },
    );

  if (isDisabled) {
    return (
      <Stack aria-disabled="true" className={cn('w-full', classes)}>
        {InnerElem}
      </Stack>
    );
  }

  if (to) {
    return (
      <Link className={classes} to={to}>
        {InnerElem}
      </Link>
    );
  }

  return (
    <Stack
      ref={rootRef}
      aria-expanded={hasFlyout ? !!isFlyoutOpen : undefined}
      aria-haspopup={hasFlyout || undefined}
      className={cn('w-full', classes)}
      component={onClick ? 'button' : 'div'}
      onClick={onClick ? () => onClick() : undefined}
      onKeyDown={onKeyDown}>
      {InnerElem}
    </Stack>
  );
};

const SidebarItem = ({
  sidebarItem,
  mode,
  isChild,
  className,
}: {
  sidebarItem: SidebarNavItem;
  mode?: SideBarMode;
  isChild?: boolean;
  className?: string;
}) => {
  const { id, to, icon, text, action, isDisabled } = sidebarItem;
  const children: SidebarNavSubItem[] = sidebarItem.children ?? [];

  const navigation = useNavigation();
  const { isMobile } = useScreen();
  const { closeFlyout, openFlyout, openFlyoutId, toggleFlyout } = useSidebarFlyout();

  const isActive =
    isSidebarItemActive(navigation.asPath, sidebarItem) ||
    children.some((child) => isSidebarItemActive(navigation.asPath, child));

  const [showItems, { toggle: setShowItems }] = useToggle(isActive);
  const [shouldAutoFocusFlyout, setShouldAutoFocusFlyout] = useState(false);

  const hasFlyout = !!children.length && !isMobile && !isChild && !isDisabled;
  const hasInlineChildren = !!children.length && !hasFlyout && !isDisabled;
  const isFlyoutOpen = openFlyoutId === id;

  const autoOpenedForPath = useRef<string | null>(null);

  useEffect(() => {
    if (!hasFlyout || !isActive || autoOpenedForPath.current === navigation.asPath) {
      return;
    }

    autoOpenedForPath.current = navigation.asPath;
    openFlyout(id);
  }, [hasFlyout, id, isActive, navigation.asPath, openFlyout]);

  useEffect(() => {
    if (!isFlyoutOpen) {
      setShouldAutoFocusFlyout(false);
    }
  }, [isFlyoutOpen]);

  const onFlyoutClose = useCallback(() => closeFlyout(id), [closeFlyout, id]);

  const onTriggerKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (OPEN_FLYOUT_KEYS.includes(event.key)) {
        event.preventDefault();
        setShouldAutoFocusFlyout(true);
        openFlyout(id);
      }
    },
    [id, openFlyout],
  );

  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverCloseTimeout = useCallback(() => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearHoverCloseTimeout, [clearHoverCloseTimeout]);

  const onFlyoutMouseEnter = useCallback(() => {
    clearHoverCloseTimeout();
    openFlyout(id);
  }, [clearHoverCloseTimeout, id, openFlyout]);

  const onFlyoutMouseLeave = useCallback(() => {
    clearHoverCloseTimeout();
    hoverCloseTimeoutRef.current = setTimeout(() => closeFlyout(id), 150);
  }, [clearHoverCloseTimeout, closeFlyout, id]);

  if (hasFlyout) {
    return (
      <div className="contents" onMouseEnter={onFlyoutMouseEnter} onMouseLeave={onFlyoutMouseLeave}>
        <Popover
          open={isFlyoutOpen}
          onOpenChange={(open: boolean) => (open ? openFlyout(id) : closeFlyout(id))}>
          {({ anchorEl, setAnchorEl }) => (
            <>
              {renderSidebarItem({
                action,
                className,
                hasFlyout,
                icon,
                isActive,
                isFlyoutOpen,
                mode,
                text,
                isExpandable: true,
                rootRef: setAnchorEl,
                onClick: () => toggleFlyout(id),
                onKeyDown: onTriggerKeyDown,
              })}
              <Popover.Content
                className="z-50"
                classes={FLYOUT_PANEL_CLASSES}
                maxWidth={FLYOUT_PANEL_WIDTH}
                minWidth={FLYOUT_PANEL_WIDTH}
                position="right-start"
                renderAs="popper">
                <FlyoutPanel
                  currentPath={navigation.asPath}
                  items={children}
                  label={text}
                  shouldAutoFocus={shouldAutoFocusFlyout}
                  triggerElement={anchorEl}
                  onClose={onFlyoutClose}
                />
              </Popover.Content>
            </>
          )}
        </Popover>
      </div>
    );
  }

  return (
    <>
      {renderSidebarItem({
        to,
        icon,
        text,
        isActive,
        isDisabled,
        action,
        mode,
        isChild,
        className,
        isExpandable: hasInlineChildren,
        showItems,
        setShowItems,
        onClick: hasInlineChildren
          ? () => {
              setShowItems?.(!showItems);
            }
          : undefined,
      })}
      {hasInlineChildren ? (
        <SidebarAccordion.LazyMotion isExpanded={showItems}>
          <SidebarAccordion.Div className="!gap-0">
            <Stack className="h-4 flex-shrink-0" />
            <Stack
              className="duration-50 linear overflow-hidden border-l border-border pl-8 transition ml-[1.7rem] flex-shrink-0"
              direction="col">
              <SidebarAccordion.LazyMotion isExpanded={showItems}>
                <SidebarAccordion.Div className="!gap-0">
                  {children.map((child) => (
                    <SidebarItem
                      key={child.id}
                      isChild
                      className="duration-50 linear transition"
                      mode={mode}
                      sidebarItem={child}
                    />
                  ))}
                </SidebarAccordion.Div>
              </SidebarAccordion.LazyMotion>
            </Stack>
            <Stack className="h-4" />
          </SidebarAccordion.Div>
        </SidebarAccordion.LazyMotion>
      ) : null}
    </>
  );
};

export default React.memo(SidebarItem);
